import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as partnerService from '../../services/partner-service';
import type { FounderDetail } from '../../services/partner-service';
import { getCurrentUser } from '../../data/session';
import Modal from '../../components/Modal';

// ──────────────────────────────────────────────────────────────────────────
// Styling helpers (mirror dashboard)
// ──────────────────────────────────────────────────────────────────────────

const industryColor: Record<string, string> = {
  Technology: 'bg-chart/10 text-chart border-chart/20',
  HealthTech: 'bg-success/10 text-success border-success/20',
  EdTech: 'bg-role-admin/10 text-role-admin border-role-admin/20',
  Logistics: 'bg-warning/10 text-warning border-warning/20',
  Finance: 'bg-warning/10 text-warning border-warning/20',
  CleanTech: 'bg-chart/10 text-chart border-chart/20',
  AgriTech: 'bg-success/10 text-success border-success/20',
};

const stageLabel: Record<string, string> = {
  idea: 'Idea',
  mvp: 'MVP',
  growth: 'Growth',
  scale: 'Scale',
};

const pitchBadge = (status: FounderDetail['pitchStatus']): string => {
  switch (status) {
    case 'approved':
      return 'bg-success/15 text-success border-success/30';
    case 'published':
      return 'bg-chart/15 text-chart border-chart/30';
    case 'submitted':
      return 'bg-warning/15 text-warning border-warning/30';
    case 'draft':
      return 'bg-surface text-muted border-line';
    default:
      return 'bg-surface text-muted border-line';
  }
};

const pitchLabel: Record<FounderDetail['pitchStatus'], string> = {
  none: 'Not Started',
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  published: 'Published',
};

const ideaLabel: Record<FounderDetail['ideaStatus'], string> = {
  none: 'Not Started',
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  revision_needed: 'Revision Needed',
};

const ideaBadge = (status: FounderDetail['ideaStatus']): string => {
  if (status === 'approved') return 'bg-success/15 text-success border-success/30';
  if (status === 'submitted') return 'bg-warning/15 text-warning border-warning/30';
  if (status === 'revision_needed') return 'bg-danger/15 text-danger border-danger/30';
  return 'bg-surface text-muted border-line';
};

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const formatDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatRelative = (iso?: string): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  if (diff < 0) {
    const futureDays = Math.round(-diff / 86_400_000);
    if (futureDays === 0) return 'Today';
    if (futureDays === 1) return 'Tomorrow';
    return `in ${futureDays} days`;
  }
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins || 1}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ──────────────────────────────────────────────────────────────────────────
// Phase pipeline visual
// ──────────────────────────────────────────────────────────────────────────

const PhasePipelineInline: React.FC<{ phases: FounderDetail['phaseStatuses'] }> = ({ phases }) => {
  return (
    <div className="flex items-start gap-0 overflow-x-auto pb-2">
      {phases.map((p, i) => {
        const done = p.status === 'completed';
        const current = p.status === 'active';
        const locked = p.status === 'locked';
        return (
          <React.Fragment key={p.id}>
            {i > 0 && (
              <div
                className={`h-0.5 w-6 md:w-10 flex-shrink-0 mt-5 ${
                  done ? 'bg-success' : current || !locked ? 'bg-chart/40' : 'bg-line'
                }`}
              />
            )}
            <div className="flex flex-col items-center gap-1.5 min-w-[90px] md:min-w-[110px] flex-shrink-0 px-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                  done
                    ? 'bg-success text-white'
                    : current
                    ? 'bg-chart text-white animate-pulse'
                    : 'bg-surface border border-line text-muted'
                }`}
              >
                {done ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  p.order
                )}
              </div>
              <span
                className={`text-[10px] md:text-xs text-center leading-tight ${
                  current || done ? 'text-text font-medium' : 'text-muted'
                }`}
              >
                {p.title}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

const PartnerStartupDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<FounderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pitchDeckOpen, setPitchDeckOpen] = useState(false);

  const backHref = useMemo(() => {
    const role = getCurrentUser().role;
    if (role === 'associate') return '/app/dashboard/associate?tab=startups';
    return '/app/dashboard/partner';
  }, []);

  useEffect(() => {
    if (!id) return;
    partnerService
      .getFounderDetail(id)
      .then((d) => {
        if (!d) setNotFound(true);
        else setDetail(d);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <p className="font-serif-display italic text-muted py-12">Loading founder details…</p>
      </div>
    );
  }

  if (notFound || !detail) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <Link
          to={backHref}
          className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to founders
        </Link>
        <div className="bg-shell border border-line rounded-card p-12 text-center">
          <p className="font-display text-2xl tracking-wide text-text uppercase mb-2">Founder not found</p>
          <p className="font-serif-display italic text-muted">
            We couldn't find a founder with that ID. They may have been removed from your cohort.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-12">
      {/* ─── Back ─── */}
      <Link
        to={backHref}
        className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to founders
      </Link>

      {/* ─── Hero ─── */}
      <div className="bg-shell border border-line rounded-card p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="flex-1 min-w-0">
            <p className="font-display text-xs tracking-display text-chart uppercase mb-1">Founder Profile</p>
            <h1 className="font-display text-3xl md:text-5xl tracking-wide text-text leading-tight">{detail.startupName}</h1>
            {detail.startupTagline && (
              <p className="font-serif-display italic text-muted text-lg md:text-xl mt-2">{detail.startupTagline}</p>
            )}

            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-full bg-chart/10 border border-chart/20 flex items-center justify-center text-chart font-bold text-xs">
                {initialsOf(detail.founderName)}
              </div>
              <div>
                <div className="font-semibold text-text text-sm">{detail.founderName}</div>
                {detail.founderRegion && <div className="text-xs text-muted">{detail.founderRegion}</div>}
              </div>

              <span
                className={`ml-2 px-2.5 py-1 rounded-full text-xs font-medium border ${
                  industryColor[detail.industry] || 'bg-surface text-muted border-line'
                }`}
              >
                {detail.industry}
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-surface text-muted border border-line">
                {stageLabel[detail.stage] || detail.stage}
              </span>
            </div>
          </div>

          <button
            onClick={() => setPitchDeckOpen(true)}
            className="inline-flex items-center justify-center gap-2 bg-chart text-white px-5 py-2.5 rounded-card font-medium text-sm hover:bg-chart/90 transition-colors shadow-sm"
          >
            View Pitch Deck
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      </div>

      {/* ─── Phase pipeline ─── */}
      <section className="bg-shell border border-line rounded-card p-6 md:p-8">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="font-display text-xs tracking-display text-chart uppercase mb-1">Incubation Journey</p>
            <h2 className="font-display text-2xl md:text-3xl tracking-wide text-text">Phase Progress</h2>
          </div>
          <div className="text-right">
            <div className="text-3xl font-display text-text tabular-nums leading-none">{detail.phaseProgressPct}%</div>
            <div className="text-xs text-muted mt-1">of current phase</div>
          </div>
        </div>

        <PhasePipelineInline phases={detail.phaseStatuses} />

        <div className="mt-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-text font-medium">
              Current: Phase {detail.currentPhase} — {detail.currentPhaseTitle}
            </span>
            <span className="text-muted tabular-nums">{detail.phaseProgressPct}%</span>
          </div>
          <div className="w-full h-2 bg-surface rounded-full overflow-hidden">
            <div
              className="h-full bg-chart rounded-full transition-all"
              style={{ width: `${detail.phaseProgressPct}%` }}
            />
          </div>
        </div>
      </section>

      {/* ─── Metric cards ─── */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-shell border border-line rounded-card p-5">
          <div className="text-[10px] uppercase tracking-wider text-muted mb-2">Coursework</div>
          <div className="text-3xl font-display text-text tabular-nums leading-none">{detail.courseworkPct}%</div>
          <div className="text-xs text-muted mt-2">of modules completed</div>
          <div className="mt-3 w-full h-1 bg-surface rounded-full overflow-hidden">
            <div className="h-full bg-success rounded-full" style={{ width: `${detail.courseworkPct}%` }} />
          </div>
        </div>

        <div className="bg-shell border border-line rounded-card p-5">
          <div className="text-[10px] uppercase tracking-wider text-muted mb-2">Mentor Sessions</div>
          <div className="text-3xl font-display text-text tabular-nums leading-none">
            {detail.sessionsInvited > 0 ? `${detail.sessionsAttended}/${detail.sessionsInvited}` : '—'}
          </div>
          <div className="text-xs text-muted mt-2">sessions attended</div>
          {detail.upcomingSessions.length > 0 && (
            <div className="text-xs text-chart mt-3 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-chart animate-pulse" />
              Next: {formatRelative(detail.upcomingSessions[0].scheduledAt)}
            </div>
          )}
        </div>

        <div className="bg-shell border border-line rounded-card p-5">
          <div className="text-[10px] uppercase tracking-wider text-muted mb-2">Idea Submission</div>
          <span
            className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${ideaBadge(detail.ideaStatus)}`}
          >
            {ideaLabel[detail.ideaStatus]}
          </span>
          {detail.ideaSubmittedAt && (
            <div className="text-xs text-muted mt-3">Submitted {formatDate(detail.ideaSubmittedAt)}</div>
          )}
        </div>

        <div className="bg-shell border border-line rounded-card p-5">
          <div className="text-[10px] uppercase tracking-wider text-muted mb-2">Pitch</div>
          <span
            className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${pitchBadge(detail.pitchStatus)}`}
          >
            {pitchLabel[detail.pitchStatus]}
          </span>
          {detail.pitchAskAmount && (
            <div className="text-xs text-muted mt-3">
              Ask: <span className="text-text font-semibold">{detail.pitchAskAmount}</span>
            </div>
          )}
        </div>
      </section>

      {/* ─── Fundamentals + sidebar ─── */}
      <section className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
        {/* Fundamentals */}
        <div className="space-y-4">
          <FundamentalCard title="About" body={detail.description} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FundamentalCard title="Problem" body={detail.problemStatement} />
            <FundamentalCard title="Solution" body={detail.solution} />
            <FundamentalCard title="Target Market" body={detail.targetMarket} />
            <FundamentalCard title="Revenue Model" body={detail.revenueModel} />
          </div>
          {detail.traction && (
            <div className="bg-chart/5 border border-chart/20 rounded-card p-5">
              <div className="text-[10px] uppercase tracking-wider text-chart mb-2 flex items-center gap-1.5">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden="true">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 18L9 11.25l4.306 4.306a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"
                  />
                </svg>
                Traction
              </div>
              <p className="text-text text-sm leading-relaxed">{detail.traction}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="bg-surface rounded-card p-5">
            <div className="text-[10px] uppercase tracking-wider text-muted mb-3">Company</div>
            <dl className="space-y-3">
              <InfoRow label="Team size" value={`${detail.teamSize} people`} />
              <InfoRow label="Founded" value={String(detail.foundingYear)} />
              {detail.website && (
                <div className="flex items-start justify-between gap-3 text-sm">
                  <dt className="text-muted">Website</dt>
                  <dd>
                    <a
                      href={detail.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-chart hover:text-chart/80 transition-colors break-all text-right"
                    >
                      {detail.website.replace(/^https?:\/\//, '')}
                    </a>
                  </dd>
                </div>
              )}
              <InfoRow label="Updated" value={formatRelative(detail.updatedAt)} />
            </dl>
          </div>

          {detail.documents.length > 0 && (
            <div className="bg-surface rounded-card p-5">
              <div className="text-[10px] uppercase tracking-wider text-muted mb-3">Documents</div>
              <ul className="space-y-2">
                {detail.documents.map((d) => (
                  <li key={d.id} className="flex items-center gap-3 text-sm">
                    <svg className="w-4 h-4 text-muted flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <div className="text-text text-sm truncate">{d.title}</div>
                      <div className="text-[10px] text-muted">{formatSize(d.fileSize)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </section>

      {/* ─── Activity timeline ─── */}
      {detail.activity.length > 0 && (
        <section>
          <h2 className="font-display text-2xl md:text-3xl tracking-wide text-text mb-5">Recent Activity</h2>
          <ol className="relative border-l border-line pl-6 space-y-5">
            {detail.activity.slice(0, 10).map((ev) => (
              <li key={ev.id} className="relative">
                <span
                  className={`absolute -left-[31px] top-1 w-3 h-3 rounded-full border-2 border-bg ${
                    ev.type === 'phase_completed' || ev.type === 'pitch_approved'
                      ? 'bg-success'
                      : ev.type === 'pitch_published'
                      ? 'bg-chart'
                      : ev.type === 'idea_submitted'
                      ? 'bg-warning'
                      : 'bg-muted'
                  }`}
                />
                <div className="text-sm text-text">{ev.title}</div>
                {ev.subtitle && <div className="text-xs text-muted italic mt-0.5">{ev.subtitle}</div>}
                <div className="text-[10px] uppercase tracking-wider text-muted mt-1">{formatRelative(ev.at)}</div>
              </li>
            ))}
          </ol>
        </section>
      )}
      <Modal isOpen={pitchDeckOpen} title="Pitch Deck" onClose={() => setPitchDeckOpen(false)}>
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-full bg-chart/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-chart" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m5-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="font-display text-2xl tracking-wide text-text mb-2">Coming Soon</h3>
          <p className="text-muted text-sm">Pitch deck viewing for partners and associates is coming in a future release.</p>
        </div>
      </Modal>
    </div>
  );
};

export default PartnerStartupDetail;

// ── Sub-components ─────────────────────────────────────────────────────

const FundamentalCard: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div className="bg-shell border border-line rounded-card p-5">
    <div className="text-[10px] uppercase tracking-wider text-muted mb-2">{title}</div>
    <p className="text-text text-sm leading-relaxed">{body || <span className="text-muted italic">Not provided</span>}</p>
  </div>
);

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <dt className="text-muted">{label}</dt>
    <dd className="text-text font-medium">{value}</dd>
  </div>
);
